import { useCallback } from 'react'
import { useReactFlow } from '@/libs/workflow-node-ui'
import { computeWorkflowCanvasLayout } from '../application/workflow-canvas-layout'
import type { CanvasNode, NodeSize } from '../application/workflow-canvas-layout'
import { activeWorkflow$, canvasEdges$, canvasNodes$, teams$ } from '../store'

const buildTeamNameMap = (): Map<string, string> => {
	const map = new Map<string, string>()
	for (const team of teams$.get()) map.set(team.id, team.name)
	return map
}

const withStoredPositions = (nodes: CanvasNode[], stored: Record<string, { x: number; y: number }>): CanvasNode[] =>
	nodes.map(node => {
		const pos = stored[node.id]
		return pos ? { ...node, position: pos } : node
	})

const useCanvasLayout = () => {
	const { getInternalNode, getNodes } = useReactFlow()

	const currentSizes = useCallback((): Map<string, NodeSize> => {
		const sizes = new Map<string, NodeSize>()
		for (const node of getNodes()) {
			const internal = getInternalNode(node.id)
			const w = internal?.measured?.width
			const h = internal?.measured?.height
			if (w && h) sizes.set(node.id, { width: w, height: h })
		}
		return sizes
	}, [getNodes, getInternalNode])

	// Sets both nodes and edges from a fresh layout, applying stored positions
	const applyLayout = useCallback((stored: Record<string, { x: number; y: number }>) => {
		const wf = activeWorkflow$.get()
		if (!wf) return
		const layout = computeWorkflowCanvasLayout(wf, buildTeamNameMap())
		canvasNodes$.set(withStoredPositions(layout.nodes, stored))
		canvasEdges$.set(layout.edges)
	}, [])

	// Recomputes layout using measured DOM sizes, preserving relative positions
	const recalculate = useCallback((stored: Record<string, { x: number; y: number }>) => {
		const wf = activeWorkflow$.get()
		if (!wf) return
		const sizes = currentSizes()
		if (sizes.size === 0) return
		const { nodes: laid, edges: laidEdges } = computeWorkflowCanvasLayout(wf, buildTeamNameMap(), sizes)
		canvasNodes$.set(withStoredPositions(laid, stored))
		canvasEdges$.set(laidEdges)
	}, [currentSizes])

	return { applyLayout, recalculate }
}

export { useCanvasLayout, buildTeamNameMap, withStoredPositions }
